// EditUserModal.tsx
import React, { useContext, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { Region, User } from '../types';
import { AppContext } from '../../AppContext';
import { useAuth0 } from '@auth0/auth0-react';

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onUpdateUser: (updatedUser: User) => void;
  regions: Region[];
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  user,
  onUpdateUser,
  regions,
}) => {
  const { user: auth0User } = useAuth0();
  const { user: currentUser, setUser } = useContext(AppContext);

  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState('');
  const [isPasswordEditable, setIsPasswordEditable] = useState(false);
  const [roleId, setRoleId] = useState(user.role_id.toString());
  const [regionId, setRegionId] = useState(user.region_id || '');

  const [saving, setSaving] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Block backdrop/Escape while saving
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (saving) return;
    onClose();
  };

  const handlePasswordEditClick = () => {
    setIsPasswordEditable(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData: any = {
        user_id: user.id,
        auth0_sub: user.auth0_sub,
        email,
        role_id: parseInt(roleId, 10),
        region_id: regionId || undefined,
        current_auth0_sub: auth0User?.sub,
      };
      if (isPasswordEditable && password) {
        updateData.password = password;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-user`,
        updateData
      );

      // Update context or parent list
      const updated: User = {
        ...user,
        email,
        role_id: parseInt(roleId, 10),
        region_id: regionId || undefined,
      };

      if (user.id === currentUser?.id) {
        setUser(updated);
      }
      onUpdateUser(updated);

      onClose();
    } catch (err: any) {
      console.error('Failed to update user', err);
      setErrorMsg(
        err.response?.data?.error || 'Error saving user. Please try again later.'
      );
      setErrorOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        BackdropProps={{ onClick: e => saving && e.stopPropagation() }}
        aria-labelledby="edit-user-dialog"
      >
        <DialogTitle id="edit-user-dialog">Edit User</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={saving}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={saving || !isPasswordEditable}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handlePasswordEditClick}
                    disabled={saving}
                  >
                    <EditIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Role"
            select
            fullWidth
            value={roleId}
            onChange={e => setRoleId(e.target.value)}
            margin="dense"
            disabled={saving}
          >
          {/* Only show SUPERADMIN if currentUserRole is 1 */}
          {currentUser ? currentUser.role_id <= 1 && <MenuItem value="1">SUPERADMIN</MenuItem> : <div />}

          {/* Only show ADMIN if currentUserRole <= 2 */}
          {currentUser ? currentUser.role_id <= 2 && <MenuItem value="2">ADMIN</MenuItem> : <div />}

          {/* Always show ADVANCED and MEMBER (lower roles) */}
          <MenuItem value="5">ADVANCED</MenuItem>
          <MenuItem value="3">MEMBER</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Region"
            select
            fullWidth
            value={regionId}
            onChange={e => setRegionId(e.target.value)}
            disabled={saving}
          >
            {regions.map(r => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={onClose}
            color="primary"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !email || !roleId}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorOpen(false)}
          severity="error"
          variant="filled"
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditUserModal;
